import { useCallback, useState } from "react"
import { useTranslation } from "react-i18next"

const useValidateAddress = (geocoder, form, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel) => {
  const [loading, setLoading] = useState(false)
  const [errorMessage, setErrorMessage] = useState("")
  const [suggestedAddress, setSuggestedAddress] = useState("")
  const [suggestedComponents, setSuggestedComponents] = useState(null)
  const { t } = useTranslation()

  const match = (component, value) => {
    if (!component || value == null) return false
    const valueLower = String(value).toLowerCase().trim()
    const longName = (component.long_name || "").toLowerCase().trim()
    const shortName = (component.short_name || "").toLowerCase().trim()
    return longName === valueLower || shortName === valueLower
  }

  const normalizeAddress = (value) => String(value || "").toLowerCase().replace(/\s+/g, " ").trim()
  const getFirstSegment = (value) => String(value || "").split(",")[0].trim()

  const getComponent = (addressComponents, types) => {
    const component = addressComponents.find((comp) => types.some((t) => comp.types.includes(t)))
    return component ? { long_name: component.long_name, short_name: component.short_name } : null
  }

  const validateAddressComponents = useCallback((place, values) => {
    const { street_address = "", city = "", state = "", country = "" } = values
    const addressComponents = place.address_components ?? []
    const streetAddressLower = String(street_address || "")
      .toLowerCase()
      .trim()

    const stateComponent = getComponent(addressComponents, ["administrative_area_level_1"])
    const countryComponent = getComponent(addressComponents, ["country"])
    const cityComponent = getComponent(addressComponents, [
      "locality",
      "sublocality",
      "administrative_area_level_1",
      "administrative_area_level_2"
    ])
    // Extract street components using short_name
    const streetNumber = getComponent(addressComponents, ["street_number"])
    const route = getComponent(addressComponents, ["route"])
    const geocodedStreet = [streetNumber?.short_name, route?.short_name].filter(Boolean).join(" ").toLowerCase().trim()

    return {
      isStreetAddress: streetAddressLower && geocodedStreet ? streetAddressLower === geocodedStreet : false,
      isZipValid: true,
      isCityValid: match(cityComponent, city),
      isStateValid: match(stateComponent, state),
      isCountryValid: match(countryComponent, country)
    }
  }, [])

  const handleConfirmAddress = useCallback(async () => {
    setLoading(true)
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
    try {
      const values = await form.validateFields()
      console.log("[ListingLocation] Confirm address submitted", { values })

      const userAddress = Object.values(values).join(", ")
      const result = await Promise.race([
        new Promise((resolve) => {
          try {
            geocoder({ address: userAddress }, (place) => {
              try {
                if (place) {
                  console.log("[ListingLocation] Geocoder response", {
                    formattedAddress: place?.formatted_address,
                    addressComponents: place?.address_components
                  })
                  const validationResults = validateAddressComponents(place, values)
                  const isValid =
                    validationResults.isCityValid &&
                    validationResults.isStateValid &&
                    validationResults.isCountryValid

                  const formatted_address = place.formatted_address ?? ""
                  const streetValue = normalizeAddress(getFirstSegment(values.street_address))
                  const formattedValue = normalizeAddress(getFirstSegment(formatted_address))
                  const suggestedValue = normalizeAddress(getFirstSegment(suggestedAddress))
                  const isStreetMatch = streetValue
                    ? formattedValue === streetValue || (suggestedValue && suggestedValue === streetValue)
                    : false

                  console.log("[ListingLocation] Address validation details", {
                    validationResults,
                    isValid,
                    streetValue,
                    formattedValue,
                    suggestedValue,
                    isStreetMatch
                  })

                  if (isValid && isStreetMatch) {
                    const location = place.geometry.location
                    setMeetingLatLng(formatted_address)
                    setCenterLatLng({ lat: location.lat(), lng: location.lng() })
                    setIsButtonDisabled(true)
                    setErrorMessage("")
                    setSuggestedAddress("")
                    setSuggestedComponents(null)
                    setButtonLabel(t("listings.tabs.location.confirmed"))
                    resolve(true)
                  } else {
                    setCenterLatLng?.(null)
                    setMeetingLatLng(null)
                    setButtonLabel(t("listings.tabs.location.confirm_address"))
                    setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                    setSuggestedAddress(formatted_address)

                    const addressComponents = place.address_components ?? []
                    const city = getComponent(addressComponents, [
                      "locality",
                      "sublocality",
                      "administrative_area_level_1",
                      "administrative_area_level_2"
                    ])
                    const state = getComponent(addressComponents, ["administrative_area_level_1"])
                    const country = getComponent(addressComponents, ["country"])
                    const zip = getComponent(addressComponents, ["postal_code"])

                    setSuggestedComponents({
                      // Address Line 1 should store the full formatted address.
                      street_address: getFirstSegment(formatted_address),
                      city: city?.short_name || city?.long_name || "",
                      state: state?.short_name || state?.long_name || "",
                      country: country?.short_name || country?.long_name || "",
                      zip_code: zip?.short_name || zip?.long_name || ""
                    })
                    resolve(false)
                  }
                } else {
                  setCenterLatLng?.(null)
                  setMeetingLatLng(null)
                  setButtonLabel(t("listings.tabs.location.confirm_address"))
                  setErrorMessage(t("listings.tabs.location.validation.address_not_verified"))
                  resolve(false)
                }
              } catch (innerErr) {
                console.error(innerErr)
                setCenterLatLng?.(null)
                setMeetingLatLng(null)
                setButtonLabel(t("listings.tabs.location.confirm_address"))
                resolve(false)
              }
            })
          } catch (outerErr) {
            console.error(outerErr)
            setCenterLatLng?.(null)
            setMeetingLatLng(null)
            setButtonLabel(t("listings.tabs.location.confirm_address"))
            resolve(false)
          }
        }),
        new Promise((resolve) => setTimeout(() => resolve(false), 10000))
      ])
      return result
    } catch (err) {
      console.error(err)
      return false
    } finally {
      setTimeout(() => {
        setLoading(false)
      }, 1000)
    }
  }, [form, geocoder, validateAddressComponents, setCenterLatLng, setMeetingLatLng, setIsButtonDisabled, setButtonLabel, suggestedAddress, t])

  const clearError = useCallback(() => {
    setErrorMessage("")
    setSuggestedAddress("")
    setSuggestedComponents(null)
  }, [])

  return { handleConfirmAddress, loading, errorMessage, suggestedAddress, suggestedComponents, clearError }
}

export default useValidateAddress
